import { useEffect, useMemo, useState } from 'react';
import { addDays, parseISO, startOfDay } from 'date-fns';
import type { AuditLogQuery } from '@/api/audit/auditApi.types';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { ALL_ACTIVITY_CATEGORIES, type ActivityCategoryFilter } from '@/constants/activityLog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

export interface ActivityLogFilterState {
  searchInput: string;
  setSearchInput: (value: string) => void;
  category: ActivityCategoryFilter;
  setCategory: (value: ActivityCategoryFilter) => void;
  fromDate: string;
  setFromDate: (value: string) => void;
  untilDate: string;
  setUntilDate: (value: string) => void;
  clearDates: () => void;
  page: number;
  setPage: (value: number) => void;
  hasActiveFilters: boolean;
  query: AuditLogQuery;
}

function convertStartOfDayToIso(dateValue: string, dayOffset: number): string | null {
  if (dateValue.trim().length === 0) {
    return null;
  }
  const parsed = parseISO(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return addDays(startOfDay(parsed), dayOffset).toISOString();
}

export function useActivityLogFilters(
  clientId: string | null,
  pageSize: number = ADMIN_LIST_PAGE_SIZE
): ActivityLogFilterState {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [category, setCategory] = useState<ActivityCategoryFilter>(ALL_ACTIVITY_CATEGORIES);
  const [fromDate, setFromDate] = useState('');
  const [untilDate, setUntilDate] = useState('');
  const [page, setPage] = useState(1);

  useEffect(
    function resetPageWhenFiltersChange() {
      setPage(1);
    },
    [debouncedSearch, category, fromDate, untilDate, clientId]
  );

  const query = useMemo<AuditLogQuery>(
    function buildQuery() {
      return {
        page,
        pageSize,
        search: debouncedSearch,
        category: category === ALL_ACTIVITY_CATEGORIES ? null : category,
        clientId,
        occurredFrom: convertStartOfDayToIso(fromDate, 0),
        occurredUntil: convertStartOfDayToIso(untilDate, 1),
      };
    },
    [page, pageSize, debouncedSearch, category, clientId, fromDate, untilDate]
  );

  return {
    searchInput,
    setSearchInput,
    category,
    setCategory,
    fromDate,
    setFromDate,
    untilDate,
    setUntilDate,
    clearDates: function clearDates() {
      setFromDate('');
      setUntilDate('');
    },
    page,
    setPage,
    hasActiveFilters:
      debouncedSearch.trim().length > 0 ||
      category !== ALL_ACTIVITY_CATEGORIES ||
      fromDate.length > 0 ||
      untilDate.length > 0,
    query,
  };
}
